<template>
  <section class="checkout" v-loading="!isConfigLoaded || loading">
    <h1 class="sr-only">Checkout</h1>
    <template v-if="isConfigLoaded">
      <div class="container-fluid" v-if="items.length">
        <form @submit.prevent="placeOrder" method="post" id="checkoutForm">
          <div class="row checkout__wrap">
            <div class="checkout__form col-xl-6 col-lg-12 col-sm-12 col-xs-12">
              <express-checkout/>
              <contact-information/>
              <shipping ref="shippingRef" @get-taxes="fetchTaxes"/>
              <billing @get-taxes="fetchTaxes"/>
              <desktop-payment-methods ref="paymentMethods"/>
            </div>
            <products :orderUnprocessable="orderUnprocessable" :optOutPromotionsText="optOutPromotionsText"/>
          </div>
        </form>
        <div class="mobile_bottombar" v-if="isHidePlaceOrderButton === false">
          <button :disabled="isRequestProcessing"
                  class="btn-common"
                  type="submit"
                  @click.prevent="placeOrder">
            Place Your Order
          </button>
        </div>

      </div>

      <div class="container-fluid" v-else-if="!items.length && !isRequestProcessing">
        <slot name="empty"></slot>
      </div>
    </template>
  </section>
</template>

<script>
import {mask} from 'vue-the-mask';
import _ from 'lodash';
import {mapActions, mapGetters} from 'vuex';
import billing from '@app/components/checkout/components/billing.vue';
import cartProducts from '@app/components/checkout/components/products.vue';
import contactInformation from '@app/components/checkout/components/contactInformation.vue';
import countryState from '@app/components/countryState.vue';
import shipping from '@app/components/checkout/components/shipping.vue';
import CheckoutApi from '@support/checkoutApi';
import '@app/components/checkout/components/paymentMethods/index';
import desktopPaymentMethods from '@app/components/checkout/components/desktopPaymentMethods';
import ExpressCheckout from '@app/components/checkout/components/expressCheckout';
import Products from '@app/components/checkout/components/products';

export default {
  components: {
    Products,
    ExpressCheckout,
    billing,
    countryState,
    cartProducts,
    contactInformation,
    shipping,
    desktopPaymentMethods,
  },
  provide() {
    return {
      $validator: this.$validator,
    };
  },
  props: {
    source: {
      default: {},
    },
    user_info: {
      default: {},
    },
    optOutPromotionsText: {
      type: String,
      default: null,
    },
  },
  directives: {mask},
  data: function () {
    return {
      //Shopping cart data
      shippingMethods: [],
      selectedShippingMethod: null,
      is_promotionable: true,
      password: null,
      promotions_agreement: true,
      recaptchaToken: null,
    };
  },
  computed: {
    ...mapGetters('checkout', [
      'checkout',
      'isProcessable',
      'orderUnprocessable',
      'isPaidByGiftCertificate',
    ]),
    payment_type: {
      get() {
        return this.$store.state.payment.payment_type;
      },
      set(p) {
        this.$store.commit('payment/setPaymentType', p);
      },
    },
    payment_data() {
      return this.$store.state.payment.payment_data;
    },
    items() {
      return this.$store.getters['checkout/items'];
    },
    isCheckoutLoaded: {
      get() {
        return this.$store.state.checkout.isCheckoutLoaded;
      },
      set(p) {
        this.$store.commit('checkout/setCheckoutLoaded', p);
      },
    },
    isRequestProcessing: {
      get() {
        return this.$store.state.checkout.isRequestProcessing;
      },
      set(p) {
        this.$store.commit('checkout/setRequestProcessing', p);
      },
    },
    showPasswordForm: {
      get() {
        return this.$store.state.checkout.showPasswordForm;
      },
      set(p) {
        this.$store.commit('checkout/setShowPasswordForm', p);
      },
    },
    isFormBlocked: {
      get() {
        return this.$store.state.checkout.isFormBlocked;
      },
      set(p) {
        this.$store.commit('checkout/setFormBlocked', p);
      },
    },
    signingUpUser: {
      get() {
        return this.$store.state.checkout.signingUpUser;
      },
      set(p) {
        this.$store.commit('checkout/setSigningUpUser', p);
      },
    },
    isLoadingShippingMethods: {
      get() {
        return this.$store.state.checkout.isLoadingShippingMethods;
      },
      set(p) {
        this.$store.commit('checkout/setLoadingShippingMethods', p);
      },
    },
    isTaxesCalculated: {
      get() {
        return this.$store.state.checkout.isTaxesCalculated;
      },
      set(p) {
        this.$store.commit('checkout/setTaxesCalculated', p);
      },
    },
    loadingTaxes: {
      get() {
        return this.$store.state.checkout.isLoadingTaxes;
      },
      set(p) {
        this.$store.commit('checkout/setLoadingTaxes', p);
      },
    },
    shipping() {
      return this.$store.state.shipping;
    },
    contact() {
      return this.$store.state.contact;
    },
    loading: function () {
      return this.isRequestProcessing;
    },
    isCheckoutRefreshRequested() {
      return this.$store.state.checkout.isCheckoutRefreshRequested;
    },
    isPlaceOrderRequested: {
      get() {
        return this.$store.state.checkout.isPlaceOrderRequested;
      },
      set(p) {
        this.$store.commit('checkout/setIsPlaceOrderRequested', p);
      },
    },
    isConfigLoaded() {
      return this.$store.state.checkout.isConfigLoaded;
    },
    selectedDesktopPaymentMethod() {
      return this.$store.state.checkout.selectedDesktopPaymentMethod;
    },
    isHidePlaceOrderButton() {
      return !this.isPaidByGiftCertificate && !this.selectedDesktopPaymentMethod ||
        this.selectedDesktopPaymentMethod.is_hide_place_order_button;
    },
    giftObject() {
      return this.$store.getters['checkout/getGiftObject'];
    },
  },
  watch: {
    'shipping.country': function (newValue, oldValue) {
      if (newValue !== oldValue && oldValue) {
        this.$store.commit('shipping/setState', '');
        this.$store.commit('shippingMethods/setShippingMethods', []);
        this.fetchTaxes();
        this.fetchShipping();
      }
    },
    'shipping.state': function (newValue, oldValue) {
      if (newValue === oldValue) {
        return;
      }

      this.$store.commit('shippingMethods/setShippingMethods', []);
      this.fetchTaxes();
      this.fetchShipping();
    },
    'shipping.zip_code': function () {
      this.$store.commit('shippingMethods/setShippingMethods', []);
      this.fetchShipping();
    },
    'shipping.city': function () {
      this.$store.commit('shippingMethods/setShippingMethods', []);
      this.fetchShipping();
    },
    isCheckoutRefreshRequested(value) {
      if (value === true) {
        this.fetchCheckout();
      }
    },
    isPlaceOrderRequested(value) {
      if (value === true) {
        this.isPlaceOrderRequested = false;
        this.placeOrder();
      }
    },
  },
  created: function () {
    this.fillUserInfo(this.user_info);
    this.fetchCheckout(() => {
      this.$nextTick(() => {
        this.fetchShipping();
      });
    });
    this.$store.commit('checkout/setValidator', this.$validator);
  },
  mounted: function () {
    this.initRecaptcha()
  },
  methods: {
    ...mapActions('checkout', [
      'fetchGroupMessages',
      'fetchCheckout',
      'fetchTaxes',
      'validateCheckout',
    ]),
    ...mapActions('shippingMethods', [
      'fetchShipping',
    ]),
    fillUserInfo: function (userInfo) {
      if (userInfo.data) {
        const user = userInfo.data;
        const setShippingGeoData = ({iso_code, state, city}) => {
          this.$store.commit('shipping/setCountry', iso_code || null);
          this.$store.commit('shipping/setState', state || null);
          this.$store.commit('shipping/setCity', city || null);
        };
        const setShippingAddress = (address) => {
          this.$store.commit('shipping/setFirstName', address.first_name || '');
          this.$store.commit('shipping/setLastName', address.last_name || '');
          this.$store.commit('shipping/setCompany', address.company || '');
          this.$store.commit('shipping/setAddressLine1', address.address_line_1 || '');
          this.$store.commit('shipping/setAddressLine2', address.address_line_2 || '');
          this.$store.commit('shipping/setCity', address.city || '');
          this.$store.commit('shipping/setCountry', address.country || '');
          this.$store.commit('shipping/setState', address.state || '');
          this.$store.commit('shipping/setZipCode', address.zip_code || '');
        };

        this.$store.commit('contact/setEmail', user.email);
        setShippingGeoData(user.geo_data);

        if (user.customer && Object.keys(user.customer.data).length) {
          const customer = user.customer.data;

          this.$store.commit('contact/setPhone', customer.phone);
          this.$store.commit('rewards/setPoints', customer.points || 0);

          this.is_promotionable = true;

          const defaultShippingAddress = customer.defaultShippingAddress.data;
          if (Object.keys(defaultShippingAddress).length) {
            setShippingAddress(defaultShippingAddress);
            this.$store.dispatch('billing/replaceState', this.shipping);
          }
        }
      }
    },
    placeOrder() {

      this.isRequestProcessing = true;

      if (this.$store.state.billing.isSameAsShipping) {
        this.$store.dispatch('billing/replaceState', this.shipping);
      }

      console.log(this.isPaidByGiftCertificate);

      if (this.isPaidByGiftCertificate) {
        console.log('paid by gift certificate');
        this.$store.commit('payment/setPaymentType', 'gift_card');
        this.$store.commit('payment/setPaymentData', this.giftObject);
      }

      this.validateCheckout().then(() => {
        const prePaymentRequests = this.$refs.paymentMethods.$children.filter(
          child => typeof child.prePaymentRequest === 'function').map(child => child.prePaymentRequest());

        if (!prePaymentRequests.length) {
          this.submitPayment();
        } else {
          Promise.all(prePaymentRequests).then((result) => {
            if (!result.length || result.includes(false)) {
              throw new Error('Payment request is not valid or rejected');
            }

            this.submitPayment();
          }).catch(() => {
            this.isRequestProcessing = false;
          });
        }
      }).catch((e) => {
        const first_error = this.$validator.errors.items[0].field;

        if (first_error) {
          this.$scrollTo(`[name="${first_error}"]`);
        }
        this.isRequestProcessing = false;
      });
    },
    submitPayment() {
      const {contact, shipping, payment_type, payment_data, slug} = this;
      const billing = this.$store.state.billing;
      this.isRequestProcessing = true;

      if ('authorize-net-credit-card' === payment_type || 'brain-tree-credit-card' === payment_type) {
        if (CheckoutApi.isCheckoutRecaptchaRequired() && null === this.recaptchaToken) {
          this.errors.add({
            field: 'recaptcha',
            msg: 'Please confirm that you are not a robot'
          });

          this.isRequestProcessing = false;

          this.$scrollTo('#recaptcha-container');

          return;
        }
      }

      const form = {
        contact,
        shipping,
        billing: {
          ...billing,
          phone: contact.phone,
          email: contact.email,
        },
        payment_data,
        payment_type,
        slug,
        c_token: this.recaptchaToken,
      };

      console.log(form);

      this.isRequestProcessing = true;

      CheckoutApi.submitPayment(form).then((response) => {
        if (response.data.status === 'success') {
          if (response.data.redirect_url) {
            window.location.href = response.data.redirect_url;
          } else {
            window.location.href = '/account/';
          }
        } else {
          this.$notify({
            type: response.data.status || 'error',
            message: response.data.message,
            title: 'Could not place your order',
            duration: 10000,
          });
          this.fetchCheckout();
        }
      }).catch((error) => {
        this.$handleError(error, 'Could not place your order. Please try again later.', 'Payment Error');
        console.error(error);
      }).finally(() => {
        this.isRequestProcessing = false;
        this.$store.commit('checkout/setIsPaymentRequested', false);
        this.$store.dispatch('payment/reset');
      });
    },
    resetForm() {
      this.$nextTick(() => this.$validator.reset());
    },
    initRecaptcha() {
      if (CheckoutApi.isCheckoutRecaptchaRequired()) {
        this.$nextTick(() => {
          const intervalId = setInterval(() => {
            const container = document.getElementById('recaptcha-container');
            if (CheckoutApi.recaptchaReady() && null !== container) {
              clearInterval(intervalId);
              window.grecaptcha.render(container, {
                sitekey: window.reCaptchaKeyV2,
                callback: (token) => {
                  this.errors.remove('recaptcha')
                  console.log('recaptcha token', token)
                  this.recaptchaToken = token;
                },
                'expired-callback': () => {
                  this.errors.add({
                    field: 'recaptcha',
                    msg: 'Please confirm that you are not a robot'
                  });
                  this.recaptchaToken = null
                }
              });
            }
          }, 250);
        });
      }
    }
  },
};
</script>
